import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, onPasswordChange, onUnauthorized, refreshAccessToken, setAccessToken } from '@/lib/api-client';
import { watchIdle } from '@/lib/session-idle';
const AuthContext = createContext(undefined);
/**
 * Déconnexion volontaire : la page quittée ne doit pas être rouverte après la
 * connexion suivante, qui peut être celle d'un autre compte (poste partagé).
 * Une session expirée, elle, ramène à la page d'origine.
 */
let explicitLogout = false;
export const wasExplicitLogout = () => explicitLogout;
/** Session fermée pour inactivité : la page de connexion l'explique. */
let idleLogout = false;
export const wasIdleLogout = () => idleLogout;
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const bootstrap = useCallback(async () => {
        try {
            // Refresh silencieux au chargement (mutualisé : React peut lancer cet effet deux fois)
            await refreshAccessToken();
            setUser(await api.auth.me());
        }
        catch {
            // Pas de session — normal
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        onUnauthorized(() => {
            setAccessToken(null);
            setUser(null);
        });
        // Mot de passe temporaire ou 2FA imposée : on relit le profil pour savoir quoi exiger
        onPasswordChange(() => {
            api.auth.me().then(setUser).catch(() => undefined);
        });
        bootstrap();
    }, [bootstrap]);
    const login = useCallback(async (input) => {
        const { accessToken } = await api.auth.login(input);
        explicitLogout = false;
        idleLogout = false;
        setAccessToken(accessToken);
        const me = await api.auth.me();
        setUser(me);
        return me;
    }, []);
    const applySession = useCallback(async (accessToken) => {
        setAccessToken(accessToken);
        const me = await api.auth.me();
        setUser(me);
        return me;
    }, []);
    const logout = useCallback(async () => {
        try {
            await api.auth.logout();
        }
        catch {
            // Ignorer — on veut nettoyer l'état local dans tous les cas
        }
        explicitLogout = true;
        setAccessToken(null);
        setUser(null);
    }, []);
    // Poste laissé sans surveillance (veille, pause) : la session se ferme comme une déconnexion volontaire
    const signedIn = !!user;
    useEffect(() => {
        if (!signedIn)
            return;
        return watchIdle(() => {
            idleLogout = true;
            logout();
        });
    }, [signedIn, logout]);
    return (_jsx(AuthContext.Provider, { value: { user, loading, login, logout, applySession }, children: children }));
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
//# sourceMappingURL=AuthContext.js.map